import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, CalendarClock, CloudSun, Flame, Loader2, Minus, Plus, Power, Thermometer } from 'lucide-react';
import authFetch from '../../utils/authFetch';
import HouseIllustration, { HOUSE_SPOTS } from './HouseIllustration';
import { strBasePath, strPropertyQuery, useStrProperty } from './properties';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

// Same screen as the guest guidebook's smart home page (dobrereal-site
// src/app/hisicapriorehu/guidebook/smarthome/page.js), plus the planned temperature changes.
const SAUNA_RULES_URL = 'https://dobrereal.com/hisicapriorehu/guidebook/wellness/rules';
const REFRESH_MS = 30000;
// Target changes are sent once the user stops tapping +/-.
const TARGET_DEBOUNCE_MS = 700;

const formatTemp = (value) => (
  value === null || value === undefined ? '–' : `${Number(value).toFixed(1).replace(/\.0$/, '')}°C`
);

const formatDateTime = (iso) => new Intl.DateTimeFormat('en-GB', {
  weekday: 'short', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Ljubljana'
}).format(new Date(iso));

const formatDate = (isoDate) => new Intl.DateTimeFormat('en-GB', {
  day: 'numeric', month: 'short', timeZone: 'UTC'
}).format(new Date(`${isoDate}T00:00:00Z`));

const SmartHomePage = () => {
  const navigate = useNavigate();
  const property = useStrProperty();
  const query = strPropertyQuery(property.key);
  const connected = property.websiteConnected;

  // loading | ready | error
  const [status, setStatus] = useState('loading');
  const [data, setData] = useState(null);
  const [actionError, setActionError] = useState('');

  const load = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/smart-home${query}`);
      if (!response.ok) throw new Error('Request failed');
      setData(await response.json());
      setStatus('ready');
    } catch {
      // A failed background refresh keeps showing the last known state.
      setStatus((prev) => (prev === 'ready' ? prev : 'error'));
    }
  }, [query]);

  useEffect(() => {
    if (!connected) return undefined;
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [connected, load]);

  const sendAction = async (deviceId, action, value) => {
    setActionError('');
    try {
      const response = await authFetch(
        `${API_BASE}/api/short-term-rentals/smart-home/devices/${encodeURIComponent(deviceId)}${query}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, value }) }
      );
      if (!response.ok) throw new Error('Request failed');
      const { device } = await response.json();
      setData((prev) => ({ ...prev, devices: prev.devices.map((d) => (d.id === device.id ? device : d)) }));
    } catch {
      setActionError('The command could not be carried out. Please try again.');
      load();
    }
  };

  // The smart home belongs to the property connected to the house.
  if (!connected) return <Navigate to={strBasePath(property)} replace />;

  const devices = data?.devices || [];
  const thermostats = devices.filter((d) => d.type === 'thermostat');
  const saunas = devices.filter((d) => d.type === 'sauna');
  const sensors = devices.filter((d) => d.type === 'sensor');
  const outdoor = sensors.find((d) => d.id === 'outdoor');
  const otherSensors = sensors.filter((d) => d !== outdoor);

  let content;
  if (status === 'loading') {
    content = <Spinner />;
  } else if (status === 'error') {
    content = (
      <div style={{ ...glass, padding: '24px' }}>
        <p style={{ color: '#fca5a5', marginTop: 0 }}>Could not load the smart home.</p>
        <button type="button" onClick={() => { setStatus('loading'); load(); }} style={retryButton}>Try again</button>
      </div>
    );
  } else {
    content = (
      <div style={stack}>
        <div style={{ position: 'relative' }}>
          <HouseIllustration
            markers={saunas.map((sauna) => ({
              id: sauna.id,
              at: HOUSE_SPOTS.wellness,
              label: sauna.name,
              value: sauna.on ? formatTemp(sauna.currentTemperature) : 'Off',
              active: sauna.on
            }))}
          />
          {outdoor && (
            <div style={{ position: 'absolute', left: 0, top: 0 }}>
              <Chip icon={<CloudSun size={16} color="#bae6fd" />} label={outdoor.name} value={formatTemp(outdoor.currentTemperature)} />
            </div>
          )}
        </div>

        {data.devicesError && <p style={errorText}>Could not reach the house. Current temperatures are not available right now.</p>}
        {actionError && <p style={errorText}>{actionError}</p>}

        {saunas.map((device) => <SaunaCard key={device.id} device={device} onAction={sendAction} />)}

        {thermostats.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            {thermostats.map((device) => <ThermostatCard key={device.id} device={device} onAction={sendAction} />)}
          </div>
        )}

        {otherSensors.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px' }}>
            {otherSensors.map((device) => (
              <div key={device.id} style={{ ...glass, padding: '20px' }}>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>{device.name}</div>
                <div style={{ fontSize: '30px', fontWeight: 700, color: 'white' }}>{formatTemp(device.currentTemperature)}</div>
              </div>
            ))}
          </div>
        )}

        <PlannedChanges plan={data.plannedChanges} />
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => navigate(strBasePath(property))} style={backButton}>
        <ArrowLeft size={16} /> Back
      </button>
      <div style={panel}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 4px', fontSize: '28px', fontWeight: 600, color: 'white' }}>Smart Home</h1>
          <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>
            Check temperatures, adjust the heating and see what is planned.
          </p>
        </div>
        {content}
      </div>
    </div>
  );
};

const STATUS_STYLES = {
  planned: { label: 'Planned', background: 'rgba(45,212,191,0.15)', color: '#99f6e4' },
  due: { label: 'Applying now', background: 'rgba(251,191,36,0.15)', color: '#fde68a' },
  applied: { label: 'Already applied', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)' },
  none: { label: 'No change', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }
};

const describeChange = (entry, rooms) => {
  if (entry.status === 'none') return 'The guest has not chosen a temperature, so the heating stays as it is.';
  if (entry.status === 'applied') return `Heating was set to ${formatTemp(entry.temperature)} in ${rooms}.`;
  return `Heating turns on at ${formatTemp(entry.temperature)} in ${rooms}.`;
};

const PlannedChanges = ({ plan }) => {
  const rooms = plan.rooms.join(', ') || 'no rooms';
  return (
    <div style={{ ...glass, padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white', fontWeight: 600, marginBottom: '14px' }}>
        <CalendarClock size={18} color="#99f6e4" />
        Planned temperature changes
      </div>

      {plan.entries.length === 0 && (
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', margin: '0 0 12px' }}>No upcoming confirmed bookings.</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
        {plan.entries.map((entry) => {
          const badge = STATUS_STYLES[entry.status];
          return (
            <div key={entry.bookingId} data-testid="planned-change" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '8px 16px', padding: '12px 14px', borderRadius: '14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: 'white', fontWeight: 600, fontSize: '14px' }}>
                  {entry.status === 'applied' ? formatDateTime(entry.appliedAt) : entry.status === 'none' ? `Arrival ${formatDate(entry.checkIn)}` : formatDateTime(entry.changeAt)}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginTop: '2px' }}>{describeChange(entry, rooms)}</div>
                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', marginTop: '2px' }}>
                  {entry.guestName}, {formatDate(entry.checkIn)} – {formatDate(entry.checkOut)}
                </div>
              </div>
              <span style={{ flexShrink: 0, borderRadius: '999px', padding: '3px 10px', fontSize: '12px', fontWeight: 600, background: badge.background, color: badge.color }}>{badge.label}</span>
            </div>
          );
        })}
      </div>

      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', lineHeight: 1.6, margin: 0 }}>
        Guests choose their arrival temperature in the guidebook. It is applied {plan.preheatHours} hours before check-in, and the guest can change it until check-in.
        Nothing changes at check-out. This list only shows changes planned by BDintelDB, not schedules set directly in Home Assistant.
      </p>
    </div>
  );
};

const Spinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
    <Loader2 size={32} color="#99f6e4" style={spin} />
    <style>{'@keyframes smart-home-spin { to { transform: rotate(360deg); } }'}</style>
  </div>
);

const Chip = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.1)', padding: '8px 12px', backdropFilter: 'blur(12px)' }}>
    {icon}
    <div style={{ lineHeight: 1.25 }}>
      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>{label}</div>
      <div style={{ fontSize: '14px', fontWeight: 600, color: 'white' }}>{value}</div>
    </div>
  </div>
);

// Local copy of the target so +/- reacts immediately; the server value wins once the change is sent.
const useTarget = (device, onAction) => {
  const [pending, setPending] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const target = pending ?? device.targetTemperature;
  const change = (delta) => {
    const base = target ?? device.minTarget;
    const next = Math.min(device.maxTarget, Math.max(device.minTarget, base + delta * device.step));
    setPending(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      await onAction(device.id, 'set_target', next);
      setPending(null);
    }, TARGET_DEBOUNCE_MS);
  };

  return { target, change };
};

const GAUGE_COLORS = { teal: ['#2dd4bf', '#bef264'], amber: ['#f59e0b', '#fde68a'] };

// 240° arc gauge. The filled part shows the set temperature within the allowed range.
const Gauge = ({ fraction, color = 'teal', children }) => {
  const gradientId = `gauge-${useId().replace(/:/g, '')}`;
  const [from, to] = GAUGE_COLORS[color];
  const point = (deg) => {
    const rad = (deg * Math.PI) / 180;
    return `${(80 + 64 * Math.cos(rad)).toFixed(2)} ${(80 + 64 * Math.sin(rad)).toFixed(2)}`;
  };
  const arc = `M ${point(150)} A 64 64 0 1 1 ${point(30)}`;
  const filled = Math.max(0, Math.min(1, fraction)) * 100;

  return (
    <div style={{ position: 'relative', margin: '0 auto', width: '176px', height: '160px' }}>
      <svg viewBox="0 0 160 145" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor={from} />
            <stop offset="100%" stopColor={to} />
          </linearGradient>
        </defs>
        <path d={arc} pathLength="100" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="12" strokeLinecap="round" />
        <path d={arc} pathLength="100" fill="none" stroke={`url(#${gradientId})`} strokeWidth="12" strokeLinecap="round" strokeDasharray={`${filled} 100`} style={{ transition: 'stroke-dasharray 0.3s' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: '8px' }}>{children}</div>
    </div>
  );
};

const RoundButton = ({ onClick, disabled, label, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} aria-label={label} style={{ ...roundButton, opacity: disabled ? 0.3 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
    {children}
  </button>
);

const TargetGauge = ({ device, onAction, color }) => {
  const { target, change } = useTarget(device, onAction);
  const fraction = target === null ? 0 : (target - device.minTarget) / (device.maxTarget - device.minTarget);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
      <RoundButton onClick={() => change(-1)} disabled={target !== null && target <= device.minTarget} label={`Decrease ${device.name}`}>
        <Minus size={18} />
      </RoundButton>
      <Gauge fraction={fraction} color={color}>
        <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.5)' }}>Current</span>
        <span style={{ fontSize: '30px', fontWeight: 600, color: 'white' }}>{formatTemp(device.currentTemperature)}</span>
        <span style={{ marginTop: '4px', borderRadius: '999px', background: 'rgba(255,255,255,0.1)', padding: '2px 10px', fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
          Set to {formatTemp(target)}
        </span>
      </Gauge>
      <RoundButton onClick={() => change(1)} disabled={target !== null && target >= device.maxTarget} label={`Increase ${device.name}`}>
        <Plus size={18} />
      </RoundButton>
    </div>
  );
};

const PowerToggle = ({ device, onAction, color = 'teal' }) => {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    await onAction(device.id, 'set_power', !device.on);
    setBusy(false);
  };
  const onColor = color === 'amber' ? '#fbbf24' : '#5eead4';
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-pressed={Boolean(device.on)}
      aria-label={`${device.name} power`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '8px', borderRadius: '999px', padding: '8px 16px', fontSize: '14px', fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
        ...(device.on ? { background: onColor, color: '#18181b', border: `1px solid ${onColor}` } : { background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.15)' })
      }}
    >
      {busy ? <Loader2 size={16} style={spin} /> : <Power size={16} />}
      {device.on ? 'On' : 'Off'}
      <style>{'@keyframes smart-home-spin { to { transform: rotate(360deg); } }'}</style>
    </button>
  );
};

const CardHeader = ({ icon, title, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white', fontWeight: 600 }}>
      {icon}
      <span>{title}</span>
    </div>
    {children}
  </div>
);

const ThermostatCard = ({ device, onAction }) => (
  <div data-testid={`device-${device.id}`} style={{ ...glass, padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
    <CardHeader icon={<Thermometer size={18} color="#99f6e4" />} title={device.name}>
      <PowerToggle device={device} onAction={onAction} />
    </CardHeader>
    <TargetGauge device={device} onAction={onAction} />
  </div>
);

const SaunaCard = ({ device, onAction }) => (
  <div data-testid={`device-${device.id}`} style={{ ...glass, padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', ...(device.on ? { background: 'radial-gradient(ellipse at top, rgba(245,158,11,0.18), transparent 70%)', borderColor: 'rgba(252,211,77,0.25)' } : {}) }}>
    <CardHeader icon={<Flame size={18} color={device.on ? '#fcd34d' : 'rgba(255,255,255,0.6)'} />} title={device.name}>
      <PowerToggle device={device} onAction={onAction} color="amber" />
    </CardHeader>
    {device.on && <p style={{ textAlign: 'center', fontSize: '14px', fontWeight: 500, color: '#fde68a', margin: 0 }}>Sauna is heating</p>}
    <TargetGauge device={device} onAction={onAction} color="amber" />
    <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, margin: 0 }}>
      The sauna needs 20–30 minutes to heat up. Please remember to turn it off when you are done.
    </p>
    <p style={{ fontSize: '14px', color: 'rgba(253,230,138,0.8)', lineHeight: 1.6, margin: 0 }}>
      Warning: Since the sauna runs on energy storage batteries, usage in low temperatures may be limited to 2-3 hours. It shuts off automatically when batteries deplete.
    </p>
    <a href={SAUNA_RULES_URL} target="_blank" rel="noreferrer" style={{ fontSize: '14px', fontWeight: 500, color: '#99f6e4', textDecoration: 'none' }}>
      Read Safety Rules →
    </a>
  </div>
);

const spin = { animation: 'smart-home-spin 1s linear infinite' };
const stack = { display: 'flex', flexDirection: 'column', gap: '20px' };
const errorText = { color: '#fca5a5', margin: 0 };
const panel = { maxWidth: '960px', margin: '0 auto', overflow: 'hidden', borderRadius: '32px', border: '1px solid rgba(255,255,255,0.1)', background: 'radial-gradient(ellipse at top, #21474a 0%, #0f2326 45%, #081315 100%)', padding: '28px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', boxSizing: 'border-box' };
const glass = { borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.06)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)', boxSizing: 'border-box' };
const backButton = { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '14px', marginBottom: '20px', padding: 0 };
const retryButton = { borderRadius: '999px', background: 'rgba(255,255,255,0.1)', border: 'none', padding: '8px 20px', color: 'white', fontWeight: 500, cursor: 'pointer' };
const roundButton = { display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: '44px', height: '44px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.1)', color: 'white' };

export default SmartHomePage;
